import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, 
  Typography, 
  TextField, 
  MenuItem, 
  Button, 
  IconButton,
  Switch, 
  FormControlLabel,
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  Chip,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import api from 'state/axios';
import { formatDate } from 'state/helpers';
import CustomDatePicker from 'components/CustomDatePicker';
import FlexBetween from 'components/FlexBetween';
import SectionLabel from 'components/SectionLabel';
import FieldDisplay from 'components/FieldDisplay';


const BLANK_QUAL = {
  post: '',
  base: '',
  acType: '',
  primaryQual: false,
  rosterGroup: 0,
  notToBeRoster: false,
  begDay: null,
  endDay: null,
};


const CrewQualifications = ({ values, setFieldValue, isEditMode }) => {
  const crewRanks = useSelector((state) => state.crewRanks);
  const params = useSelector((state) => state.params);
  const [acTypes, setAcTypes] = useState([]);

  useEffect(() => {
    api.get('/actypes/active')
      .then((res) => setAcTypes(res.data || []))
      .catch(() => {});
  }, []);

  const sortedRanks = crewRanks
    ?.slice()
    .sort((a, b) => (a.rankPriority ?? Infinity) - (b.rankPriority ?? Infinity)) || [];

  const instructors = [
    ...(params?.find(p => p.param === 'FLIGHT_CREW_INSTRUCTORS')?.value?.split(',').map(s => s.trim()) || []),
    ...(params?.find(p => p.param === 'CABIN_CREW_INSTRUCTORS')?.value?.split(',').map(s => s.trim()) || []),
  ];

  const quals = values.crewQuals || [];
  const addQual    = () => setFieldValue('crewQuals', [...quals, { ...BLANK_QUAL }]);
  const removeQual = (i) => setFieldValue('crewQuals', quals.filter((_, idx) => idx !== i));
  const updateQual = (i, field, val) =>
    setFieldValue('crewQuals', quals.map((q, idx) => idx === i ? { ...q, [field]: val } : q));

  if (!isEditMode) return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Language 1" value={values.language1} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Language 2" value={values.language2} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Aircraft Qualification" value={values.quals} />
      </Box>
      {values.trainingQuals?.length > 0 && (
        <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
          <Typography variant="caption" color="text.secondary">
            Training Qualifications
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {values.trainingQuals?.map((q, i) => (
              <Chip 
                key={i} 
                variant="outlined" 
                size="small" 
                label={q} 
              />
            ))}
          </Box>
        </Box>
      )}
      
      <Box 
        display="flex" 
        flexDirection="column" 
        gap={1} 
        sx={{ gridColumn: 'span 12' }}
      >
        <SectionLabel>Crew Qualifications</SectionLabel>        
        {quals.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No qualifications recorded.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Post</TableCell>
                  <TableCell>Base</TableCell>
                  <TableCell>ACType</TableCell>
                  <TableCell>Primary</TableCell>
                  <TableCell>RosterGroup</TableCell>
                  <TableCell>NotToRoster</TableCell>
                  <TableCell>BegDay</TableCell>
                  <TableCell>EndDay</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quals.map((q, i) => (
                  <TableRow key={i}>
                    <TableCell>{q.post || '-'}</TableCell>
                    <TableCell>{q.base || '-'}</TableCell>
                    <TableCell>
                      {q.acType ? <Chip label={q.acType} size="small" color="primary" variant="outlined" /> : '-'}
                    </TableCell>
                    <TableCell>{q.primaryQual ? '✓' : ''}</TableCell>
                    <TableCell>{q.rosterGroup ?? '-'}</TableCell>
                    <TableCell>{q.notToBeRoster ? '✓' : ''}</TableCell>
                    <TableCell>{formatDate(q.begDay)}</TableCell>
                    <TableCell>{formatDate(q.endDay)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      
      <TextField
        fullWidth
        size="small"
        label="Language 1"
        name="language1"
        value={values.language1 || ''}
        onChange={(e) => setFieldValue('language1', e.target.value)}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />
      <TextField
        fullWidth
        size="small"
        label="Language 2"
        name="language2"
        value={values.language2 || ''}
        onChange={(e) => setFieldValue('language2', e.target.value)}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />

      <TextField 
        fullWidth 
        select 
        size="small" 
        label="Training Qualifications" 
        name="trainingQuals"
        value={values.trainingQuals || []}
        onChange={(e) => setFieldValue('trainingQuals', e.target.value)}
        slotProps={{ select: { multiple: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        {instructors.map((q, i) => <MenuItem key={i} value={q}>{q}</MenuItem>)}
      </TextField>

      <TextField 
        fullWidth 
        size="small" 
        label="Aircraft Qualification" 
        name="quals"
        value={values.quals} 
        onChange={(e) => setFieldValue('quals', e.target.value)}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />
      
      <Box sx={{ gridColumn: 'span 12' }}>
        <FlexBetween>
          <SectionLabel>Crew Qualifications</SectionLabel>   
          <Button 
            size="small" 
            startIcon={<Add />} 
            onClick={addQual}
          >
            Add Qualification
          </Button>
        </FlexBetween>
        {quals.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No qualifications recorded.
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {quals.map((q, i) => (
              <Box 
                key={i} 
                display="flex" 
                alignItems="center" 
                gap={2}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
              >
                <Box 
                  flex={1}
                  display="grid" 
                  gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
                  gap={2}
                >
                  <TextField 
                    fullWidth 
                    select 
                    size="small" 
                    label="Post"
                    value={q.post || ''}
                    onChange={(e) => updateQual(i, 'post', e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 2' } }}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {sortedRanks.map(r => (
                      <MenuItem key={r._id} value={r.rankCode}>
                        {r.rankCode}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField 
                    fullWidth 
                    select 
                    size="small" 
                    label="AC Type"
                    value={q.acType || ''}
                    onChange={(e) => updateQual(i, 'acType', e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 2' } }}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {acTypes.map(ac => (
                      <MenuItem key={ac.acTypeCode} value={ac.acTypeCode}>
                        {ac.acTypeCode}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField 
                    fullWidth 
                    size="small" 
                    label="Base"
                    value={q.base}
                    onChange={(e) => updateQual(i, 'base', e.target.value)}
                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 2' } }}
                  />
                  <TextField 
                    fullWidth 
                    size="small" 
                    label="Roster Group" 
                    type="number"
                    value={q.rosterGroup}
                    onChange={(e) => updateQual(i, 'rosterGroup', Number(e.target.value))}
                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 2' } }}
                  />
                  <CustomDatePicker 
                    variant="outlined" 
                    size="small" 
                    label="Beg Day" 
                    name="begDay"
                    value={q.begDay ? new Date(q.begDay) : null}
                    onChange={(_, d) => updateQual(i, 'begDay', d)}
                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 2' } }}
                  />
                  <CustomDatePicker 
                    variant="outlined" 
                    size="small" 
                    label="End Day" 
                    name="endDay"
                    value={q.endDay ? new Date(q.endDay) : null}
                    onChange={(_, d) => updateQual(i, 'endDay', d)}
                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 2' } }}
                  />
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gridColumn: { xs: 'span 12', sm: 'span 2' } 
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch 
                          size="small" 
                          checked={q.primaryQual}
                          onChange={(e) => updateQual(i, 'primaryQual', e.target.checked)} 
                        />
                      }
                      label="Primary"
                    />
                  </Box>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gridColumn: { xs: 'span 12', sm: 'span 2' } 
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch 
                          size="small" 
                          checked={q.notToBeRoster}
                          onChange={(e) => updateQual(i, 'notToBeRoster', e.target.checked)} 
                        />
                      }
                      label="Not to Roster"
                    />
                  </Box>
                </Box>
                <Box display="flex" justifyContent="flex-end">
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={() => removeQual(i)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CrewQualifications;
